import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PlanPackageDialog from './PlanPackageDialog';
import DashboardNavbar from '../../dashboardNavbar';
import DataTable from '../../ui/DataTable';
import GroupedRowActionSheet, { ICON_PROPS } from '../../ui/GroupedRowActionSheet';
import { Pencil } from 'lucide-react';
import { useConfirm } from '../../ui/ConfirmProvider';
import { SetTabs, SetTab, SectionHead } from '../../ui/primitives';
import {
  INSTRUCTOR_PLAN_LIST_DEFAULTS,
  INSTRUCTOR_PLAN_SORT_FIELDS,
  LEARNER_PLAN_LIST_DEFAULTS,
  LEARNER_PLAN_SORT_FIELDS,
  getNextPackagesSort,
  sortInstructorPlansLocally,
  sortLearnerPlansLocally,
} from './packagesListApi';
import {
  fetchInstructorPlanPackages,
  fetchLearnerPlanPackages,
  saveInstructorPlanPackage,
  saveLearnerPlanPackage,
} from './packagesAdminApi';
import { formatPlanLabel, formatUsdFromCents } from './planDisplayUtils';
import { PlanFeatureTags, PlanUsageMetrics } from './PlanEntitlementSummary';
import { SYSTEM_TABS } from '../adminSectionTabs';
import useDemoActionGate from '../../hooks/useDemoActionGate';
import DemoLockedDialog from '../../ui/DemoLockedDialog';

const EMPTY_FORM = {
  tier: '',
  name: '',
  monthlyCents: 0,
  annualCents: 0,
  currency: 'usd',
  entitlements: [],
};

const MENU_ITEMS = SYSTEM_TABS;

function buildFormFromRow(row) {
  return {
    ...EMPTY_FORM,
    ...row,
    entitlements: Array.isArray(row.entitlements)
      ? row.entitlements.map((item) => ({ ...item }))
      : [],
  };
}

function Grids() {
  const askConfirmation = useConfirm();
  const { guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [searchParams, setSearchParams] = useSearchParams();
  const packageKind = searchParams.get('kind') === 'learner' ? 'learner' : 'instructor';

  const [instructorRows, setInstructorRows] = useState([]);
  const [learnerRows, setLearnerRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKind, setDialogKind] = useState('instructor');
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [learnerSortBy, setLearnerSortBy] = useState(LEARNER_PLAN_LIST_DEFAULTS.sortBy);
  const [learnerSortDir, setLearnerSortDir] = useState(LEARNER_PLAN_LIST_DEFAULTS.sortDir);
  const [instructorSortBy, setInstructorSortBy] = useState(INSTRUCTOR_PLAN_LIST_DEFAULTS.sortBy);
  const [instructorSortDir, setInstructorSortDir] = useState(INSTRUCTOR_PLAN_LIST_DEFAULTS.sortDir);

  const setPackageKind = (kind) => {
    setSearchParams({ kind });
  };

  const loadInstructorPackages = useCallback(async () => {
    const rows = await fetchInstructorPlanPackages();
    setInstructorRows(rows);
  }, []);

  const loadLearnerPackages = useCallback(async () => {
    const rows = await fetchLearnerPlanPackages();
    setLearnerRows(rows);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadInstructorPackages(), loadLearnerPackages()])
      .catch((error) => {
        if (!cancelled) {
          console.error('Error fetching package catalogs:', error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadInstructorPackages, loadLearnerPackages]);

  const sortedLearnerRows = useMemo(
    () => sortLearnerPlansLocally(learnerRows, learnerSortBy, learnerSortDir),
    [learnerRows, learnerSortBy, learnerSortDir],
  );

  const sortedInstructorRows = useMemo(
    () => sortInstructorPlansLocally(instructorRows, instructorSortBy, instructorSortDir),
    [instructorRows, instructorSortBy, instructorSortDir],
  );

  const handleLearnerSortChange = (columnKey) => {
    const next = getNextPackagesSort(columnKey, learnerSortBy, learnerSortDir);
    setLearnerSortBy(next.sortBy);
    setLearnerSortDir(next.sortDir);
  };

  const handleInstructorSortChange = (columnKey) => {
    const next = getNextPackagesSort(columnKey, instructorSortBy, instructorSortDir);
    setInstructorSortBy(next.sortBy);
    setInstructorSortDir(next.sortDir);
  };

  const openEditDialog = (kind, row) => {
    if (!guardAction('action.system.package.edit')) return;
    setDialogKind(kind);
    setForm(buildFormFromRow(row));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleEntitlementChange = (index, value) => {
    setForm((current) => {
      const entitlements = [...(current.entitlements || [])];
      if (index < 0 || !entitlements[index]) {
        return current;
      }
      entitlements[index] = { ...entitlements[index], value: String(value) };
      return { ...current, entitlements };
    });
  };

  const handleSubmit = async () => {
    if (!guardAction('action.system.package.edit')) return;
    setLoading(true);
    try {
      const confirmed = await askConfirmation({
        title: dialogKind === 'learner' ? 'Update learner plan?' : 'Update instructor plan?',
        message: 'Save pricing, limits, features, and all entitlement values for this tier?',
        confirmLabel: 'Save',
      });
      if (!confirmed) {
        return;
      }

      const payload = {
        name: form.name,
        monthlyCents: Number(form.monthlyCents) || 0,
        annualCents: Number(form.annualCents) || 0,
        currency: form.currency || 'usd',
        entitlements: form.entitlements || [],
      };

      if (dialogKind === 'learner') {
        await saveLearnerPlanPackage(form.tier, payload);
        await loadLearnerPackages();
      } else {
        await saveInstructorPlanPackage(form.tier, payload);
        await loadInstructorPackages();
      }
      closeDialog();
    } catch (error) {
      console.error('Error saving plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const planPrimary = (row) => ({
    label: formatPlanLabel(row.name || row.tier),
    sub: `Tier · ${formatPlanLabel(row.tier)}`,
  });

  return (
    <>
    <div>
      <DashboardNavbar menuItems={MENU_ITEMS} />
      <div style={{ width: 'min(1180px, 95%)', margin: '0 auto' }}>
        <SectionHead style={{ marginTop: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22 }}>Packages</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted, #64748b)', fontSize: 14 }}>
              Super-admin control over instructor and learner plan pricing and capabilities.
            </p>
          </div>
        </SectionHead>

        <SetTabs>
          <SetTab
            $on={packageKind === 'instructor'}
            onClick={() => setPackageKind('instructor')}
          >
            Instructor packages
          </SetTab>
          <SetTab
            $on={packageKind === 'learner'}
            onClick={() => setPackageKind('learner')}
          >
            Learner packages
          </SetTab>
        </SetTabs>

        {packageKind === 'instructor' ? (
          <DataTable
            rows={sortedInstructorRows}
            loading={loading}
            rowKey={(row) => row.tier}
            sortBy={instructorSortBy}
            sortDir={instructorSortDir}
            onSortChange={handleInstructorSortChange}
            search
            searchPlaceholder="Search instructor plans…"
            searchKeys={['name', 'tier']}
            primary={{
              header: 'Plan',
              sortKey: INSTRUCTOR_PLAN_SORT_FIELDS.name,
              label: (row) => planPrimary(row).label,
              sub: (row) => planPrimary(row).sub,
            }}
            columns={[
              { header: 'Monthly', sortKey: INSTRUCTOR_PLAN_SORT_FIELDS.monthlyCents, render: (row) => formatUsdFromCents(row.monthlyCents), mono: true },
              { header: 'Annual', sortKey: INSTRUCTOR_PLAN_SORT_FIELDS.annualCents, render: (row) => formatUsdFromCents(row.annualCents), mono: true },
              {
                header: 'Usage limits',
                width: '168px',
                render: (row) => (
                  <PlanUsageMetrics entitlements={row.entitlements} planKind="instructor" />
                ),
              },
              {
                header: 'Features',
                width: '220px',
                render: (row) => <PlanFeatureTags entitlements={row.entitlements} />,
              },
            ]}
            actionsColumnWidth={52}
            actions={(row) => (
              <GroupedRowActionSheet
                entityLabel="Instructor plan"
                title={planPrimary(row).label}
                menuAriaLabel="Instructor plan actions"
                groupLabel="For this plan"
                groupItems={[
                  { key: 'edit', icon: <Pencil {...ICON_PROPS} />, label: 'Edit plan', ...lockProps('action.system.package.edit') },
                ]}
                onAction={(key) => {
                  if (key === 'edit') {
                    openEditDialog('instructor', row);
                  }
                }}
              />
            )}
            emptyText="No instructor plans found in instructor_plan_pricing."
          />
        ) : (
          <DataTable
            rows={sortedLearnerRows}
            loading={loading}
            rowKey={(row) => row.tier}
            sortBy={learnerSortBy}
            sortDir={learnerSortDir}
            onSortChange={handleLearnerSortChange}
            search
            searchPlaceholder="Search learner plans…"
            searchKeys={['name', 'tier']}
            primary={{
              header: 'Plan',
              sortKey: LEARNER_PLAN_SORT_FIELDS.name,
              label: (row) => planPrimary(row).label,
              sub: (row) => planPrimary(row).sub,
            }}
            columns={[
              { header: 'Monthly', sortKey: LEARNER_PLAN_SORT_FIELDS.monthlyCents, render: (row) => formatUsdFromCents(row.monthlyCents), mono: true },
              { header: 'Annual', sortKey: LEARNER_PLAN_SORT_FIELDS.annualCents, render: (row) => formatUsdFromCents(row.annualCents), mono: true },
              {
                header: 'Usage limits',
                width: '168px',
                render: (row) => (
                  <PlanUsageMetrics entitlements={row.entitlements} planKind="learner" />
                ),
              },
              {
                header: 'Features',
                width: '220px',
                render: (row) => <PlanFeatureTags entitlements={row.entitlements} />,
              },
            ]}
            actionsColumnWidth={52}
            actions={(row) => (
              <GroupedRowActionSheet
                entityLabel="Learner plan"
                title={planPrimary(row).label}
                menuAriaLabel="Learner plan actions"
                groupLabel="For this plan"
                groupItems={[
                  { key: 'edit', icon: <Pencil {...ICON_PROPS} />, label: 'Edit plan', ...lockProps('action.system.package.edit') },
                ]}
                onAction={(key) => {
                  if (key === 'edit') {
                    openEditDialog('learner', row);
                  }
                }}
              />
            )}
            emptyText="No learner plans found in learner_plan_pricing."
          />
        )}
      </div>

      <PlanPackageDialog
        open={dialogOpen}
        planKind={dialogKind}
        form={form}
        onChange={setForm}
        onEntitlementChange={handleEntitlementChange}
        onSubmit={handleSubmit}
        onClose={closeDialog}
        loading={loading}
      />
    </div>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}

export default Grids;
